import {
  addCheckpoint,
  checkpointFlagsInRect,
  findCheckpoint,
  findCheckpointAt,
  moveFlag,
  removeCheckpoint,
} from './checkpoints.ts';
import { cloneRgba, type Rgba } from '../color.ts';
import { clampDepth, clampElevation, clampToBounds, clonePoint, isNear } from './document.ts';
import {
  addIsland,
  clearIslandEdgeBend,
  findIsland,
  findIslandAt,
  removeIsland,
  removeVertex,
} from './islands.ts';
import {
  addRock,
  clearRockEdgeBend,
  findRock,
  findRockAt,
  removeRock,
  removeRockVertex,
} from './rocks.ts';
import {
  addShallowWaterZone,
  clearShallowWaterEdgeBend,
  defaultDepthForMaterial,
  findShallowWater,
  findShallowWaterAt,
  removeShallowWaterVertex,
  removeShallowWaterZone,
} from './shallowWater.ts';
import {
  addRefs,
  emptySelection,
  hasRef,
  normalizeRect,
  pruneSelection,
  refsInRect,
  selectedFlag,
  selectionOf,
  selectionOfMany,
  soleRef,
  toggleRef,
  type ObjectRef,
  type Rect,
  type Selection,
  type SubSelection,
} from './selection.ts';
import {
  clearParkArea,
  clearParkAreaEdgeBend,
  findParkAreaAt,
  removeParkAreaVertex,
  setParkArea,
} from './parkArea.ts';
import {
  clearStartArea,
  clearStartAreaEdgeBend,
  findStartAreaAt,
  removeStartAreaVertex,
  setStartArea,
} from './startArea.ts';
import {
  copyRefs,
  duplicateRefs,
  isClipboardEmpty,
  pasteClipboard,
  rotateRefs,
  scaleRefs,
  translateRefs,
  type ScaleRefsOptions,
} from './transform.ts';
import { findElevatedBodyAt } from './propPick.ts';
import { SNAP_GRID, snapPoint } from './snap.ts';
import {
  DEFAULT_TERRACE_ELEV_RAND,
  DEFAULT_TERRACE_COLOR_VARIATION,
  DEFAULT_TERRACE_JITTER,
  DEFAULT_TERRACE_KIND,
  DEFAULT_TERRACE_LEVELS,
  DEFAULT_TERRACE_PEAK_M,
  DEFAULT_TERRACE_RADIUS,
  DEFAULT_TERRACE_RADIUS_RAND,
  clampTerraceLevels,
  clampTerraceRadius,
  clampTerraceRandomness,
  defaultTerraceColor,
  placeTerraces,
  terraceColorForKind,
} from './terrace.ts';
import {
  addRoute,
  findRoute,
  findRouteAt,
  removeRoute,
  removeRouteVertex,
} from './routes.ts';
import {
  MIN_RING_VERTICES,
  MIN_ROUTE_VERTICES,
  type CheckpointFlag,
  type EditorSession,
  type EditorTool,
  type MapDocument,
  type PropId,
  type TerraceKind,
  type Vec2,
  type WaterMaterial,
} from './types.ts';

const DRAFT_TOOLS: ReadonlySet<EditorTool> = new Set([
  'draw-island',
  'draw-rock',
  'draw-terrace',
  'draw-water',
  'edit-start-area',
  'edit-park-area',
]);

const KEEP_SELECTION_TOOLS: ReadonlySet<EditorTool> = new Set([
  'select',
  'hand',
  'scale',
  'rotate',
]);

/** How far a duplicate or paste lands from its source, so the copy is visibly separate. */
export const DUPLICATE_OFFSET = 40;

export function createSession(): EditorSession {
  return {
    tool: 'select',
    draft: [],
    draftMaterial: 'sand',
    draftDepth: defaultDepthForMaterial('sand'),
    draftProp: 'palm-a',
    draftTerraceKind: DEFAULT_TERRACE_KIND,
    draftTerraceLevels: DEFAULT_TERRACE_LEVELS,
    draftTerracePeakM: DEFAULT_TERRACE_PEAK_M,
    draftTerraceRadius: DEFAULT_TERRACE_RADIUS,
    draftTerraceColor: defaultTerraceColor(DEFAULT_TERRACE_KIND),
    draftTerraceColorVariation: DEFAULT_TERRACE_COLOR_VARIATION,
    draftTerraceJitter: DEFAULT_TERRACE_JITTER,
    draftTerraceElevRand: DEFAULT_TERRACE_ELEV_RAND,
    draftTerraceRadiusRand: DEFAULT_TERRACE_RADIUS_RAND,
    checkpointDraft: null,
    selection: emptySelection(),
    clipboard: null,
    snapToGrid: false,
  };
}

export function setSnapToGrid(session: EditorSession, snap: boolean): void {
  session.snapToGrid = snap;
}

/** The grid step in force, or 0 when snapping is off, so callers can pass it straight to snapValue. */
export function snapStep(session: EditorSession): number {
  return session.snapToGrid ? SNAP_GRID : 0;
}

// --- Selection ------------------------------------------------------------

export function clearSelection(session: EditorSession): void {
  session.selection = emptySelection();
}

/** Replaces the selection with a single object, optionally focusing one of its parts. */
export function selectOnly(session: EditorSession, ref: ObjectRef, sub?: SubSelection): void {
  session.selection = selectionOf(ref, sub);
}

export function selectMany(session: EditorSession, refs: readonly ObjectRef[]): void {
  session.selection = selectionOfMany(refs);
}

export function toggleSelection(session: EditorSession, ref: ObjectRef): void {
  session.selection = toggleRef(session.selection, ref);
}

export function extendSelection(session: EditorSession, refs: readonly ObjectRef[]): void {
  session.selection = addRefs(session.selection, refs);
}

/** Called after any structural edit, so the selection can never point at a deleted object. */
export function reconcileSelection(session: EditorSession, map: MapDocument): void {
  session.selection = pruneSelection(session.selection, map);
}

export function selectAll(
  session: EditorSession,
  map: MapDocument,
  hiddenRouteIds?: ReadonlySet<string>,
): void {
  selectMany(
    session,
    refsInRect(
      map,
      { minX: 0, minY: 0, maxX: map.bounds.width, maxY: map.bounds.height },
      1,
      false,
      hiddenRouteIds,
    ),
  );
}

/** Marquee drag. `additive` keeps the existing selection, for shift-drag. */
export function selectInRect(
  session: EditorSession,
  map: MapDocument,
  a: Vec2,
  b: Vec2,
  additive = false,
  poleScale = 1,
  elevate = false,
  hiddenRouteIds?: ReadonlySet<string>,
): ObjectRef[] {
  const rect = normalizeRect(a, b);
  const found = refsInRect(map, rect, poleScale, elevate, hiddenRouteIds);

  if (additive) {
    extendSelection(session, found);
    return found;
  }

  if (found.length === 1 && found[0]?.kind === 'checkpoint') {
    const flags = checkpointFlagsInRect(map, found[0].id, rect, poleScale);
    selectCheckpoint(session, found[0].id, flags.length === 1 ? flags[0] : null);
    return found;
  }

  selectMany(session, found);
  return found;
}

export function selectIsland(
  session: EditorSession,
  id: string,
  vertexIndex: number | null = null,
  edgeIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'island', id }, subFromIndices(vertexIndex, edgeIndex));
}

export function selectRock(
  session: EditorSession,
  id: string,
  vertexIndex: number | null = null,
  edgeIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'rock', id }, subFromIndices(vertexIndex, edgeIndex));
}

export function selectWater(
  session: EditorSession,
  id: string,
  vertexIndex: number | null = null,
  edgeIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'water', id }, subFromIndices(vertexIndex, edgeIndex));
}

export function selectStartArea(
  session: EditorSession,
  vertexIndex: number | null = null,
  edgeIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'startArea' }, subFromIndices(vertexIndex, edgeIndex));
}

export function selectParkArea(
  session: EditorSession,
  vertexIndex: number | null = null,
  edgeIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'parkArea' }, subFromIndices(vertexIndex, edgeIndex));
}

export function selectRoute(
  session: EditorSession,
  id: string,
  vertexIndex: number | null = null,
): void {
  selectOnly(session, { kind: 'route', id }, subFromIndices(vertexIndex, null));
}

export function selectCheckpoint(
  session: EditorSession,
  id: string,
  flag: CheckpointFlag | null = null,
): void {
  selectOnly(
    session,
    { kind: 'checkpoint', id },
    flag ? { kind: 'flag', flag } : { kind: 'none' },
  );
}

/**
 * Shift/ctrl-click a pole: add or remove that pole the same way objects toggle. The second pole
 * of an already-selected gate is added so both move together; clicking the only selected pole
 * drops the checkpoint.
 */
export function toggleCheckpointFlag(session: EditorSession, id: string, flag: CheckpointFlag): void {
  const ref = { kind: 'checkpoint', id } as const;

  if (!hasRef(session.selection, ref)) {
    if (session.selection.refs.length === 0) {
      selectCheckpoint(session, id, flag);
    } else {
      extendSelection(session, [ref]);
    }

    return;
  }

  if (session.selection.refs.length > 1) {
    toggleSelection(session, ref);
    return;
  }

  const current = selectedFlag(session.selection, ref);

  if (current === null) {
    selectCheckpoint(session, id, flag === 'a' ? 'b' : 'a');
    return;
  }

  if (current === flag) {
    clearSelection(session);
    return;
  }

  selectCheckpoint(session, id, null);
}

function subFromIndices(vertexIndex: number | null, edgeIndex: number | null): SubSelection {
  if (vertexIndex !== null) {
    return { kind: 'vertex', index: vertexIndex };
  }

  if (edgeIndex !== null) {
    return { kind: 'edge', index: edgeIndex };
  }

  return { kind: 'none' };
}

// --- Drafts and tools -----------------------------------------------------

export function cancelDraft(session: EditorSession): void {
  session.draft = [];
}

export function cancelCheckpointDraft(session: EditorSession): void {
  session.checkpointDraft = null;
}

export function setDraftMaterial(session: EditorSession, material: WaterMaterial): void {
  session.draftMaterial = material;
  session.draftDepth = defaultDepthForMaterial(material);
}

export function setDraftDepth(session: EditorSession, depth: number): void {
  session.draftDepth = clampDepth(depth);
}

export function setDraftProp(session: EditorSession, prop: PropId): void {
  session.draftProp = prop;
}

export function setDraftTerraceKind(session: EditorSession, kind: TerraceKind): void {
  session.draftTerraceColor = terraceColorForKind(kind, session.draftTerraceColor);
  session.draftTerraceKind = kind;
}

export function setDraftTerraceLevels(session: EditorSession, levels: number): void {
  session.draftTerraceLevels = clampTerraceLevels(levels);
}

export function setDraftTerracePeakM(session: EditorSession, peakM: number): void {
  session.draftTerracePeakM = clampElevation(peakM);
}

export function setDraftTerraceRadius(session: EditorSession, radius: number): void {
  session.draftTerraceRadius = clampTerraceRadius(radius);
}

export function setDraftTerraceColor(session: EditorSession, color: Rgba): void {
  session.draftTerraceColor = cloneRgba(color);
}

export function setDraftTerraceColorVariation(session: EditorSession, variation: number): void {
  session.draftTerraceColorVariation = clampTerraceRandomness(variation);
}

export function setDraftTerraceJitter(session: EditorSession, jitter: number): void {
  session.draftTerraceJitter = clampTerraceRandomness(jitter);
}

export function setDraftTerraceElevRand(session: EditorSession, elevRand: number): void {
  session.draftTerraceElevRand = clampTerraceRandomness(elevRand);
}

export function setDraftTerraceRadiusRand(session: EditorSession, radiusRand: number): void {
  session.draftTerraceRadiusRand = clampTerraceRandomness(radiusRand);
}

export function setTool(session: EditorSession, tool: EditorTool): void {
  if (session.tool === tool) {
    return;
  }

  if (session.tool === 'draw-route' || tool === 'draw-route' || !DRAFT_TOOLS.has(tool)) {
    cancelDraft(session);
  }

  if (tool !== 'place-checkpoint') {
    cancelCheckpointDraft(session);
  }

  if (!KEEP_SELECTION_TOOLS.has(tool)) {
    clearSelection(session);
  }

  session.tool = tool;
}

export function appendDraftVertex(session: EditorSession, map: MapDocument, point: Vec2): Vec2 {
  const vertex = clampToBounds(clonePoint(point), map.bounds);
  session.draft.push(vertex);
  return vertex;
}

export function canCloseDraft(session: EditorSession): boolean {
  return session.draft.length >= MIN_RING_VERTICES;
}

export function closeDraft(session: EditorSession, map: MapDocument): boolean {
  if (!canCloseDraft(session)) {
    return false;
  }

  if (session.tool === 'draw-island') {
    const island = addIsland(map, session.draft);
    cancelDraft(session);
    selectIsland(session, island.id);
    return true;
  }

  if (session.tool === 'draw-rock') {
    const rock = addRock(map, session.draft);
    cancelDraft(session);
    selectRock(session, rock.id);
    return true;
  }

  if (session.tool === 'draw-terrace') {
    const refs = placeTerraces(map, session.draft, {
      kind: session.draftTerraceKind,
      levels: session.draftTerraceLevels,
      peakM: session.draftTerracePeakM,
      radius: session.draftTerraceRadius,
      color: session.draftTerraceColor,
      colorVariation: session.draftTerraceColorVariation,
      jitter: session.draftTerraceJitter,
      elevRand: session.draftTerraceElevRand,
      radiusRand: session.draftTerraceRadiusRand,
    });
    cancelDraft(session);

    if (refs.length === 0) {
      return false;
    }

    selectMany(session, refs);
    setTool(session, 'select');
    return true;
  }

  if (session.tool === 'draw-water') {
    const zone = addShallowWaterZone(
      map,
      session.draft,
      session.draftMaterial,
      session.draftDepth,
    );
    cancelDraft(session);
    selectWater(session, zone.id);
    return true;
  }

  if (session.tool === 'edit-start-area') {
    setStartArea(map, session.draft);
    cancelDraft(session);
    selectStartArea(session);
    return true;
  }

  if (session.tool === 'edit-park-area') {
    setParkArea(map, session.draft);
    cancelDraft(session);
    selectParkArea(session);
    return true;
  }

  return false;
}

export function canFinishRoute(session: EditorSession): boolean {
  return session.tool === 'draw-route' && session.draft.length >= MIN_ROUTE_VERTICES;
}

export function finishRoute(session: EditorSession, map: MapDocument): boolean {
  if (!canFinishRoute(session)) {
    return false;
  }

  const route = addRoute(map, session.draft);
  cancelDraft(session);
  selectRoute(session, route.id);
  return true;
}

export function tryPlaceRoutePoint(
  session: EditorSession,
  map: MapDocument,
  point: Vec2,
): 'appended' | 'ignored' {
  if (session.tool !== 'draw-route') {
    return 'ignored';
  }

  appendDraftVertex(session, map, snapped(session, point));
  return 'appended';
}

export function tryPlaceDrawPoint(
  session: EditorSession,
  map: MapDocument,
  point: Vec2,
  closeRadius: number,
): 'appended' | 'closed' | 'ignored' {
  if (!DRAFT_TOOLS.has(session.tool)) {
    return 'ignored';
  }

  const first = session.draft[0];

  // Closing is tested against the raw pointer so a snapped first vertex is still easy to hit.
  if (first && canCloseDraft(session) && isNear(first, point, closeRadius)) {
    closeDraft(session, map);
    return 'closed';
  }

  appendDraftVertex(session, map, snapped(session, point));
  return 'appended';
}

export function beginCheckpointDraft(session: EditorSession, point: Vec2): void {
  session.checkpointDraft = clonePoint(snapped(session, point));
}

export function finishCheckpointDraft(session: EditorSession, map: MapDocument, point: Vec2): boolean {
  const a = session.checkpointDraft;

  if (!a) {
    return false;
  }

  const checkpoint = addCheckpoint(map, a, snapped(session, point));
  session.checkpointDraft = null;
  selectCheckpoint(session, checkpoint.id);
  return true;
}

function snapped(session: EditorSession, point: Vec2): Vec2 {
  return session.snapToGrid ? snapPoint(point, SNAP_GRID) : point;
}

// --- Hit testing ----------------------------------------------------------

/** Topmost object under `point`, in the same priority order the editor draws handles. */
export function refAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
  poleScale = 1,
  elevate = false,
  hiddenRouteIds?: ReadonlySet<string>,
): ObjectRef | null {
  const checkpoint = findCheckpointAt(map, point, radius, poleScale);

  if (checkpoint) {
    return { kind: 'checkpoint', id: checkpoint.id };
  }

  const route = findRouteAt(map, point, radius, hiddenRouteIds);

  if (route) {
    return { kind: 'route', id: route.id };
  }

  if (elevate) {
    const column = findElevatedBodyAt(map, point);

    if (column) {
      return { kind: column.kind, id: column.body.id };
    }
  } else {
    // Rocks sit above islands, so a rock overlapping an island wins the click.
    const rock = findRockAt(map, point);

    if (rock) {
      return { kind: 'rock', id: rock.id };
    }

    const island = findIslandAt(map, point);

    if (island) {
      return { kind: 'island', id: island.id };
    }
  }

  const water = findShallowWaterAt(map, point);

  if (water) {
    return { kind: 'water', id: water.id };
  }

  if (findStartAreaAt(map, point)) {
    return { kind: 'startArea' };
  }

  return findParkAreaAt(map, point) ? { kind: 'parkArea' } : null;
}

/**
 * Click-to-select. With `additive` the clicked object is toggled in or out of the selection;
 * otherwise it replaces it. Clicking an already-selected object without `additive` keeps the
 * whole selection, so a click-and-drag can move the group rather than collapsing it to one item.
 */
export function trySelectAt(
  session: EditorSession,
  map: MapDocument,
  point: Vec2,
  radius: number,
  additive = false,
  poleScale = 1,
  elevate = false,
  hiddenRouteIds?: ReadonlySet<string>,
): boolean {
  const ref = refAt(map, point, radius, poleScale, elevate, hiddenRouteIds);

  if (!ref) {
    if (!additive) {
      clearSelection(session);
    }

    return false;
  }

  if (additive) {
    if (ref.kind === 'checkpoint') {
      const hit = findCheckpointAt(map, point, radius, poleScale);

      if (hit?.flag) {
        toggleCheckpointFlag(session, ref.id, hit.flag);
        return hasRef(session.selection, ref);
      }
    }

    toggleSelection(session, ref);
    return hasRef(session.selection, ref);
  }

  if (hasRef(session.selection, ref) && session.selection.refs.length > 1) {
    return true;
  }

  if (ref.kind === 'checkpoint') {
    const hit = findCheckpointAt(map, point, radius, poleScale);
    const flag = hit?.flag ?? null;

    if (hasRef(session.selection, ref) && session.selection.refs.length === 1) {
      const current = selectedFlag(session.selection, ref);

      if (flag === null) {
        selectCheckpoint(session, ref.id, null);
        return true;
      }

      if (current === null || current === flag) {
        return true;
      }
    }

    selectCheckpoint(session, ref.id, flag);
    return true;
  }

  if (ref.kind === 'route') {
    const hit = findRouteAt(map, point, radius, hiddenRouteIds);
    selectRoute(session, ref.id, hit?.vertexIndex ?? null);
    return true;
  }

  selectOnly(session, ref);
  return true;
}

// --- Moving ---------------------------------------------------------------

/**
 * Moves the selection. A lone selected gate pole moves on its own; a whole-gate or multi-object
 * selection translates every referenced object together.
 */
export function moveSelection(session: EditorSession, map: MapDocument, delta: Vec2): Vec2 {
  const ref = soleRef(session.selection);

  if (ref?.kind === 'checkpoint') {
    const flag = selectedFlag(session.selection, ref);

    if (flag) {
      const checkpoint = findCheckpoint(map, ref.id);
      const from = checkpoint?.[flag];

      if (!from) {
        return { x: 0, y: 0 };
      }

      const start = { x: from.x, y: from.y };
      moveFlag(map, ref.id, flag, { x: start.x + delta.x, y: start.y + delta.y });
      return { x: checkpoint[flag].x - start.x, y: checkpoint[flag].y - start.y };
    }
  }

  return translateRefs(map, session.selection.refs, delta);
}

/** Scales the current selection about `origin`. Factors are clamped by `scaleRefs`. */
export function scaleSelection(
  session: EditorSession,
  map: MapDocument,
  origin: Vec2,
  scaleX: number,
  scaleY: number,
  options?: ScaleRefsOptions,
): { scaleX: number; scaleY: number } {
  return scaleRefs(map, session.selection.refs, origin, scaleX, scaleY, options);
}

/** Rotates the current selection about `origin`. Returns 0 when the turn would leave the map. */
export function rotateSelection(
  session: EditorSession,
  map: MapDocument,
  origin: Vec2,
  radians: number,
): number {
  return rotateRefs(map, session.selection.refs, origin, radians);
}

// --- Clipboard ------------------------------------------------------------

export function copySelection(session: EditorSession, map: MapDocument): boolean {
  const clipboard = copyRefs(map, session.selection.refs);

  if (isClipboardEmpty(clipboard)) {
    return false;
  }

  session.clipboard = clipboard;
  return true;
}

export function cutSelection(session: EditorSession, map: MapDocument): boolean {
  if (!copySelection(session, map)) {
    return false;
  }

  return deleteSelectedObjects(session, map);
}

/** Pastes the clipboard offset from its original position, and selects what was pasted. */
export function pasteFromClipboard(session: EditorSession, map: MapDocument): boolean {
  if (!session.clipboard || isClipboardEmpty(session.clipboard)) {
    return false;
  }

  const created = pasteClipboard(map, session.clipboard, {
    x: DUPLICATE_OFFSET,
    y: DUPLICATE_OFFSET,
  });

  if (created.length === 0) {
    return false;
  }

  selectMany(session, created);
  return true;
}

export function duplicateSelection(session: EditorSession, map: MapDocument): boolean {
  const created = duplicateRefs(map, session.selection.refs, {
    x: DUPLICATE_OFFSET,
    y: DUPLICATE_OFFSET,
  });

  if (created.length === 0) {
    return false;
  }

  selectMany(session, created);
  return true;
}

// --- Deleting -------------------------------------------------------------

/**
 * Delete. With a single object and a focused vertex or edge, this edits that part instead of
 * removing the object — removing a vertex or straightening a bent edge. Otherwise it removes
 * every selected object.
 */
export function deleteSelection(session: EditorSession, map: MapDocument): boolean {
  const sole = soleRef(session.selection);

  if (sole && deleteSubSelection(session, map, sole)) {
    return true;
  }

  return deleteSelectedObjects(session, map);
}

function deleteSubSelection(session: EditorSession, map: MapDocument, ref: ObjectRef): boolean {
  const sub = session.selection.sub;

  if (sub.kind === 'edge') {
    if (ref.kind === 'island' && clearIslandEdgeBend(map, ref.id, sub.index)) {
      selectIsland(session, ref.id);
      return true;
    }

    if (ref.kind === 'rock' && clearRockEdgeBend(map, ref.id, sub.index)) {
      selectRock(session, ref.id);
      return true;
    }

    if (ref.kind === 'water' && clearShallowWaterEdgeBend(map, ref.id, sub.index)) {
      selectWater(session, ref.id);
      return true;
    }

    if (ref.kind === 'startArea' && clearStartAreaEdgeBend(map, sub.index)) {
      selectStartArea(session);
      return true;
    }

    if (ref.kind === 'parkArea' && clearParkAreaEdgeBend(map, sub.index)) {
      selectParkArea(session);
      return true;
    }

    return false;
  }

  if (sub.kind !== 'vertex') {
    return false;
  }

  if (ref.kind === 'island' && removeVertex(map, ref.id, sub.index)) {
    const island = findIsland(map, ref.id);
    selectIsland(session, ref.id, Math.min(sub.index, (island?.vertices.length ?? 1) - 1));
    return true;
  }

  if (ref.kind === 'rock' && removeRockVertex(map, ref.id, sub.index)) {
    const rock = findRock(map, ref.id);
    selectRock(session, ref.id, Math.min(sub.index, (rock?.vertices.length ?? 1) - 1));
    return true;
  }

  if (ref.kind === 'water' && removeShallowWaterVertex(map, ref.id, sub.index)) {
    const zone = findShallowWater(map, ref.id);
    selectWater(session, ref.id, Math.min(sub.index, (zone?.vertices.length ?? 1) - 1));
    return true;
  }

  if (ref.kind === 'startArea' && removeStartAreaVertex(map, sub.index)) {
    selectStartArea(session, Math.min(sub.index, (map.startArea?.vertices.length ?? 1) - 1));
    return true;
  }

  if (ref.kind === 'parkArea' && removeParkAreaVertex(map, sub.index)) {
    selectParkArea(session, Math.min(sub.index, (map.parkArea?.vertices.length ?? 1) - 1));
    return true;
  }

  if (ref.kind === 'route' && removeRouteVertex(map, ref.id, sub.index)) {
    const route = findRoute(map, ref.id);
    selectRoute(session, ref.id, Math.min(sub.index, (route?.points.length ?? 1) - 1));
    return true;
  }

  return false;
}

function deleteSelectedObjects(session: EditorSession, map: MapDocument): boolean {
  let removed = false;

  for (const ref of session.selection.refs) {
    switch (ref.kind) {
      case 'island':
        removed = removeIsland(map, ref.id) || removed;
        break;
      case 'rock':
        removed = removeRock(map, ref.id) || removed;
        break;
      case 'water':
        removed = removeShallowWaterZone(map, ref.id) || removed;
        break;
      case 'startArea':
        removed = clearStartArea(map) || removed;
        break;
      case 'parkArea':
        removed = clearParkArea(map) || removed;
        break;
      case 'checkpoint':
        removed = removeCheckpoint(map, ref.id) || removed;
        break;
      case 'route':
        removed = removeRoute(map, ref.id) || removed;
        break;
    }
  }

  if (removed) {
    clearSelection(session);
  }

  return removed;
}

export type { ObjectRef, Rect, Selection, SubSelection };
